<template>
  <v-form ref="form" v-model="valid" @submit.prevent="submitForm">
    <v-container>
      <v-row v-if="subtitle">
        <v-col class="subtitle-1">{{ subtitle }}</v-col>
      </v-row>
      <v-row v-for="(field, name) in formFields" :key="name">
        <v-col cols="12">
          <v-text-field
            v-if="field.type === 'string'"
            v-model="formData[name]"
            :label="field.label"
            :type="field.subType"
            :rules="field.rules"
            :hint="field.description"
            persistent-hint
            :counter="field.max"
            dense
          ></v-text-field>

          <v-select
            v-else-if="field.type === 'enum'"
            v-model="formData[name]"
            :items="field.options"
            :label="field.label"
            :rules="field.rules"
            :hint="field.description"
            persistent-hint
            dense
          ></v-select>

          <v-checkbox
            v-else-if="field.type === 'boolean'"
            v-model="formData[name]"
            :label="field.label"
            :rules="field.rules"
            :hint="field.description"
            persistent-hint
            dense
          ></v-checkbox>

          <template
            v-else-if="field.type === 'multistring'"
          >
            <v-text-field
              v-model="tmp[name]"
              :label="field.label"
              :rules="field.itemRules"
              @keyup.enter="addItem(name)"
              :hint="field.description"
              persistent-hint
              dense
            ></v-text-field>
            <div>
              <v-chip
                v-for="(item, i) in formData[name]"
                :key="i"
                class="ma-2"
                close
                @click:close="formData[name].splice(i, 1)"
              >
                {{ item }}
              </v-chip>
            </div>
          </template>

          <DynamicTable
            :title="field.label"
            :schema="field.schema"
            :options="field.tableOptions"
            :formOptions="field.formOptions"
            v-model="formData[name]"
            v-else-if="field.type === 'table'"
          ></DynamicTable>
          <DynamicForm
            v-else-if="field.type === 'object'"
            v-model="formData[name]"
            :schema="field.schema"
            :options="field.options"
            :hideSubmit="true"
            :subtitle="field.label"
          ></DynamicForm>
        </v-col>
      </v-row>

      <v-btn v-if="!hideSubmit" class="my-4" large color="primary" :disabled="!valid" @click="submitForm">Guardar</v-btn>
    </v-container>
  </v-form>
</template>

<script>
import redaApp from '../api/redaApp'
import DynamicTable from '@/components/DynamicTable.vue'

const isObjectEmpty = (obj) => Object.keys(obj).length === 0

export default {
  name: 'DynamicForm',
  components: {
    DynamicTable
  },
  props: {
    subtitle: String,
    schema: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: Object,
      default: () => ({})
    },
    default: {
      type: Object,
      default: () => ({})
    },
    hideSubmit: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      formData: this.value,
      formFields: {},
      formDataDefault: {},
      tmp: {},
      valid: true
    }
  },
  created () {
    this.generateFormFields()
  },
  watch: {
    value (newVal, oldVal) {
      this.formData = newVal
    },
    formData: {
      handler (val) {
        if (!val) return
        this.$emit('input', val)
      },
      deep: true
    }
  },
  methods: {
    async validate () {
      return this.$refs.form.validate()
    },

    addItem (name) {
      if (!this.tmp[name]) return
      this.formData[name].push(this.tmp[name]) // Agregar el valor
      this.tmp[name] = '' // Limpiar el campo correctamente
    },

    getRules (def) {
      const rules = []

      if (def.flags && def.flags.presence === 'required') {
        rules.push(v => !!v || 'Este campo es obligatorio')
      }

      if (def.rules && def.rules.length) {
        for (let index = 0; index < def.rules.length; index++) {
          const rule = def.rules[index]
          if (rule.name === 'min') {
            if (def.type === 'array') {
              rules.push(v => v.length >= rule.args.limit || `Debe contener almenos ${rule.args.limit} ${rule.args.limit === 1 ? 'item' : 'items'}`)
            } else if (def.type === 'string') {
              rules.push(v => v.length <= rule.args.limit || `La cantidad de caracteres debe ser mayor o igual a ${rule.args.limit}`)
            } else {
              rules.push(v => v >= rule.args.limit || `Debe ser mayor o igual a ${rule.args.limit}`)
            }
          }
          if (rule.name === 'max') {
            if (def.type === 'array') {
              rules.push(v => v.length <= rule.args.limit || `Debe contener máximo ${rule.args.limit} ${rule.args.limit === 1 ? 'item' : 'items'}`)
            } else if (def.type === 'string') {
              rules.push(v => v.length <= rule.args.limit || `La cantidad de caracteres debe ser menor o igual a ${rule.args.limit}`)
            } else {
              rules.push(v => v <= rule.args.limit || `Debe ser menor o igual a ${rule.args.limit}`)
            }
          }
        }
      }
      return rules
    },

    generateFormFields () {
      const details = this.schema.keys
      const formData = {}

      Object.entries(details).forEach(([name, def]) => {
        if (this.exclude.includes(name)) return
        const field = {
          label: def.flags?.label || name,
          type: 'string',
          rules: [],
          description: def.flags?.description || null
        }
        let value = def.flags?.default || null

        field.rules.push(...this.getRules(def))
        const max = def.rules?.find(({ name }) => name === 'max')?.args.limit
        if (max) field.max = max

        if (def.metas && def.metas.length && def.metas[0].collection) {
          const collection = def.metas[0].collection
          field.type = 'enum'
          field.options = []
          redaApp.getValidItems(collection)
            .then((items) => { field.options.push(...items) })
            .catch((e) => { console.error(e) })
        } else if (def.type === 'number') {
          field.subType = 'number'
        } else if (def.type === 'boolean') {
          field.type = 'boolean'
          value = !!value
        } else if (def.valids || def.allow?.length > 1) {
          field.type = 'enum'
          const values = def.metas?.length ? def.metas[0]?.values : null
          field.options = values || def.valids || def.allow
        } else if (def.type === 'array' && def.items?.length) {
          const item = def.items[0]
          value = value || []
          if (item.type === 'string') {
            field.type = 'multistring'
            field.itemRules = this.getRules(item)
            this.$set(this.tmp, name, '')
          } else if (item.type === 'object') {
            field.type = 'table'
            field.schema = item
            field.tableOptions = def.metas?.length ? (def.metas[0].tableOptions || {}) : {}
            field.formOptions = def.metas?.length && (def.metas[0].form || {})
          }
        } else if (def.type === 'object') {
          field.type = 'object'
          field.schema = def
          field.options = def.metas?.length && def.metas[0].form
          value = value || {}
        }

        this.$set(this.formFields, name, field)
        this.$set(this.formDataDefault, name, value)
        formData[name] = value
      })
      if (!this.formData || isObjectEmpty(this.formData)) this.formData = Object.assign({}, formData)
    },

    async submitForm () {
      const valid = await this.validate()
      if (valid) {
        const payload = JSON.parse(JSON.stringify(this.formData))
        this.exclude.forEach((key) => {
          if (payload[key]) delete payload[key]
        })
        this.$emit('submit', payload)
        if (isObjectEmpty(this.value)) this.formData = Object.assign({}, this.formDataDefault)
      }
    }
  },
  computed: {
    exclude () {
      return Object.entries(this.options)
        .map(([key, value]) => (value.exclude && key)).filter((v) => !!v)
    }
  }
}
</script>
